import clsx from 'clsx';
import React from 'react';
import QuickLink, { buildAnalyticsLocations } from '../basic/quickLink';
import {
  DataAnalytics,
  DataAnalyticsClicks,
  DataAnalyticsLocations,
  DataAnalyticsTypes,
  buildAnalytics,
} from './analytics';
import useSplunkLogger from '../../common/hooks/useSplunkLogger';

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  link?: string;
  shape?: 'rounded' | 'rectangular';
  border?: boolean;
  dataAnalytics?: DataAnalytics;
  aClass?: string;
}

// 1️⃣ Make CreateButton forward its ref to the <button>
const CreateButton = React.forwardRef<HTMLButtonElement, IButtonProps & { asChild?: boolean }>(
  ({ shape, border, className, children, type, dataAnalytics, asChild = false, ...props }, ref) => {
    const {splunkLogger} = useSplunkLogger()

    const dataAnalyticsLocation = buildAnalyticsLocations(dataAnalytics?.locations,splunkLogger);
    const dataAnalyticsClick = dataAnalytics?.click ?? DataAnalyticsClicks.NOT_AVAILABLE;
    
    const classes = clsx(
      'flex justify-center items-center transition',
      {
        'rounded-full': shape === 'rounded',
        'rounded-md': shape === 'rectangular',
        'border border-solid': border,
      },
      className,
    );

    // Non-interactive visual only (inside link)
    if (asChild) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          className={classes}
          data-analytics-type={dataAnalytics?.type}
          data-analytics-click={dataAnalyticsClick}
          data-analytics-location={dataAnalyticsLocation}
        >
          {children}
        </span>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type ?? 'button'}
        data-analytics-type={dataAnalytics?.type}
        data-analytics-click={dataAnalyticsClick}
        data-analytics-location={dataAnalyticsLocation}
        className={classes}
        {...props}
      >
        {children}
      </button>
    );
  },
);
CreateButton.displayName = 'CreateButton';

// 2️⃣ Make Button forward its ref down into CreateButton
const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  ({ link, dataAnalytics, aClass, ...props }, ref) => {
    const analytics = dataAnalytics || buildAnalytics(DataAnalyticsLocations.Body);
    analytics.type = DataAnalyticsTypes.Button;

    if (!link) {
      return <CreateButton ref={ref} {...props} dataAnalytics={analytics} />;
    }

    return (
      <QuickLink href={link} dataAnalytics={analytics}  className = {clsx("inline-flex items-center", aClass)}>
        <CreateButton ref={ref} {...props} asChild dataAnalytics={analytics} />
      </QuickLink>
    );
  },
);
Button.displayName = 'Button';

export default Button;
